import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { obterSala, Sala } from '../../services/salaService';
import { criarReserva, verificarConflito } from '../../services/reservaService';
import { format } from 'date-fns';
import SkeletonLoader from '../common/SkeletonLoader';
import { useTheme } from '../../contexts/ThemeContext';
import { corSalaParaTexto } from '../../utils/corSala';

// Gerar opções de horário de 30 em 30 minutos (00:00 até 23:30)
const gerarOpcoesHorario = (): string[] => {
  const opcoes: string[] = [];
  for (let hora = 0; hora < 24; hora++) {
    for (let minuto = 0; minuto < 60; minuto += 30) {
      const horaStr = String(hora).padStart(2, '0');
      const minutoStr = String(minuto).padStart(2, '0');
      opcoes.push(`${horaStr}:${minutoStr}`);
    }
  }
  return opcoes;
};

const FormularioReserva: React.FC = () => {
  const { salaId } = useParams<{ salaId: string }>();
  const { tema } = useTheme();
  const corTexto = (cor?: string) => corSalaParaTexto(cor || '#3498db', tema === 'escuro');
  const navigate = useNavigate();
  
  const [sala, setSala] = useState<Sala | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(false);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    data_inicio: '',
    hora_inicio: '',
    data_fim: '',
    hora_fim: '',
    observacoes: ''
  });

  const opcoesHorario = gerarOpcoesHorario();

  useEffect(() => {
    if (salaId) {
      carregarSala();
    }
  }, [salaId]);

  const carregarSala = async () => {
    if (!salaId) return;
    
    try {
      setCarregando(true);
      setErro('');
      const salaData = await obterSala(salaId);
      setSala(salaData);
      
      // Deixar campos vazios - usuário preenche manualmente
      setFormData(prev => ({
        ...prev,
        data_inicio: format(new Date(), 'yyyy-MM-dd'),
        hora_inicio: '',
        data_fim: format(new Date(), 'yyyy-MM-dd'),
        hora_fim: ''
      }));
    } catch (error: any) {
      console.error('Erro ao carregar sala:', error);
      setErro(error.response?.data?.error || 'Erro ao carregar sala');
    } finally {
      setCarregando(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErro('');
    setSucesso(false);
  };

  const validarFormulario = (): string | null => {
    if (!formData.titulo.trim()) {
      return 'Título é obrigatório';
    }
    
    if (!formData.data_inicio || !formData.hora_inicio || !formData.data_fim || !formData.hora_fim) {
      return 'Data e hora de início e fim são obrigatórias';
    }
    
    const dataInicio = new Date(`${formData.data_inicio}T${formData.hora_inicio}`);
    const dataFim = new Date(`${formData.data_fim}T${formData.hora_fim}`);
    
    if (isNaN(dataInicio.getTime()) || isNaN(dataFim.getTime())) {
      return 'Data ou hora inválida';
    }
    
    if (dataFim <= dataInicio) {
      return 'Data/hora de fim deve ser posterior à data/hora de início';
    }
    
    // Validar com margem de 10 minutos para evitar problemas de sincronia
    const agora = new Date();
    agora.setMinutes(agora.getMinutes() - 10);
    if (dataInicio < agora) {
      return 'Não é possível criar reserva no passado';
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const erroValidacao = validarFormulario();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }
    
    if (!salaId) return;
    
    try {
      setSalvando(true);
      setErro('');
      
      // Criar strings de data no formato ISO sem conversão de timezone
      const dataInicioStr = `${formData.data_inicio}T${formData.hora_inicio}:00`;
      const dataFimStr = `${formData.data_fim}T${formData.hora_fim}:00`;
      
      // Verificar conflito
      const temConflito = await verificarConflito(
        salaId,
        dataInicioStr,
        dataFimStr
      );
      
      if (temConflito) {
        setErro('Já existe uma reserva para esta sala neste horário. Por favor, escolha outro horário.');
        return;
      }
      
      // Criar reserva
      await criarReserva({
        sala_id: salaId,
        titulo: formData.titulo,
        descricao: formData.descricao || undefined,
        data_inicio: dataInicioStr,
        data_fim: dataFimStr,
        observacoes: formData.observacoes || undefined
      });
      
      setSucesso(true);
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate(`/reservas/calendario/${salaId}`);
      }, 2000);
      
    } catch (error: any) {
      console.error('Erro ao criar reserva:', error);
      setErro(error.response?.data?.error || 'Erro ao criar reserva');
    } finally {
      setSalvando(false);
    }
  };

  const handleVoltar = () => {
    navigate(`/reservas/calendario/${salaId}`);
  };

  if (carregando) {
    return <SkeletonLoader />;
  }

  if (!sala) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Sala não encontrada</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleVoltar}
          sx={{ mb: 2 }}
        >
          Voltar
        </Button>
        
        <Typography variant="h4" gutterBottom>
          Nova Reserva - {sala.nome}
        </Typography>
        <Chip
          label={sala.tipo}
          size="small"
          sx={{
            bgcolor: `${sala.cor}20`,
            color: corTexto(sala.cor),
            fontWeight: 'bold'
          }}
        />
      </Box>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {erro && (
                <Alert severity="error">{erro}</Alert>
              )}
              
              {sucesso && (
                <Alert severity="success">
                  Reserva criada com sucesso! Redirecionando...
                </Alert>
              )}

              <TextField
                label="Título da Reserva"
                fullWidth
                required
                value={formData.titulo}
                onChange={(e) => handleChange('titulo', e.target.value)}
                placeholder="Ex: Reunião de Planejamento"
                disabled={salvando || sucesso}
              />

              <TextField
                label="Descrição"
                fullWidth
                multiline
                rows={3}
                value={formData.descricao}
                onChange={(e) => handleChange('descricao', e.target.value)}
                placeholder="Descreva o objetivo da reserva (opcional)"
                disabled={salvando || sucesso}
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Data e Hora de Início
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    fullWidth
                    required
                    value={formData.data_inicio}
                    onChange={(e) => handleChange('data_inicio', e.target.value)}
                    disabled={salvando || sucesso}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Horário"
                    fullWidth
                    required
                    value={formData.hora_inicio}
                    onChange={(e) => handleChange('hora_inicio', e.target.value)}
                    disabled={salvando || sucesso}
                    placeholder="Ex: 14:30"
                    select
                    SelectProps={{
                      native: false,
                      displayEmpty: true,
                    }}
                    InputProps={{
                      startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  >
                    <MenuItem value="">
                      <em>Selecione ou digite o horário</em>
                    </MenuItem>
                    {opcoesHorario.map((horario) => (
                      <MenuItem key={horario} value={horario}>
                        {horario}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Data e Hora de Término
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    fullWidth
                    required
                    value={formData.data_fim}
                    onChange={(e) => handleChange('data_fim', e.target.value)}
                    disabled={salvando || sucesso}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Horário"
                    fullWidth
                    required
                    value={formData.hora_fim}
                    onChange={(e) => handleChange('hora_fim', e.target.value)}
                    disabled={salvando || sucesso}
                    placeholder="Ex: 15:30"
                    select
                    SelectProps={{
                      native: false,
                      displayEmpty: true,
                    }}
                    InputProps={{
                      startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  >
                    <MenuItem value="">
                      <em>Selecione ou digite o horário</em>
                    </MenuItem>
                    {opcoesHorario.map((horario) => (
                      <MenuItem key={horario} value={horario}>
                        {horario}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Box>

              <TextField
                label="Observações"
                fullWidth
                multiline
                rows={2}
                value={formData.observacoes}
                onChange={(e) => handleChange('observacoes', e.target.value)}
                placeholder="Informações adicionais (opcional)"
                disabled={salvando || sucesso}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleVoltar}
                  disabled={salvando || sucesso}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={salvando ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={salvando || sucesso}
                  sx={{ 
                    bgcolor: sala.cor,
                    '&:hover': {
                      bgcolor: sala.cor,
                      filter: 'brightness(0.9)'
                    }
                  }}
                >
                  {salvando ? 'Salvando...' : 'Criar Reserva'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FormularioReserva;
